(function initializePmgMediaFilter(root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.PmgMediaFilter = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function createPmgMediaFilter() {
  'use strict';

  const PMG_DIRECTIVE_START = /\[pmg(?:-|:)/gi;
  const PMG_DIRECTIVE = /\[pmg(?:-([^:\]\s]+))?:([^\]]*)\]/gi;
  const UNSORTED = Number.MAX_SAFE_INTEGER;

  function normalizePmgValue(value) {
    return String(value == null ? '' : value)
      .trim()
      .toLowerCase()
      .replace(/&amp;/gi, '&')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function normalizeOptions(options) {
    if (!Array.isArray(options)) return [];
    return options.map(normalizePmgValue).filter(Boolean);
  }

  function createIssue(code, detail) {
    return detail ? { code, detail } : { code };
  }

  function parseSort(rawSort, issues) {
    if (rawSort == null) return null;
    if (!/^\d+$/.test(rawSort)) {
      issues.push(createIssue('invalid-sort', rawSort));
      return null;
    }

    const sort = Number(rawSort);
    if (!Number.isSafeInteger(sort)) {
      issues.push(createIssue('invalid-sort', rawSort));
      return null;
    }

    return sort;
  }

  function parseTagPart(rawPart, rawTag, issues) {
    const part = String(rawPart || '').trim();
    if (!part) {
      issues.push(createIssue('empty-tag-part', rawTag));
      return null;
    }
    if (part === '*') return '*';

    const rawChoices = part.split('&');
    const choices = rawChoices.map(normalizePmgValue);
    if (choices.some((choice) => !choice)) {
      const issueCode = rawChoices.length > 1 ? 'empty-tag-choice' : 'empty-tag-part';
      issues.push(createIssue(issueCode, rawTag));
      return null;
    }

    return choices.join('&');
  }

  function parsePmgTags(rawText) {
    const text = String(rawText == null ? '' : rawText);
    const directiveStarts = text.match(PMG_DIRECTIVE_START) || [];
    const hasDirective = directiveStarts.length > 0;
    const tags = [];
    const issues = [];
    let parsedDirectiveCount = 0;
    let match;

    PMG_DIRECTIVE.lastIndex = 0;
    while ((match = PMG_DIRECTIVE.exec(text)) !== null) {
      parsedDirectiveCount += 1;
      const sort = parseSort(match[1], issues);
      const rawTag = String(match[2] || '').trim();

      if (!rawTag) {
        issues.push(createIssue('empty-tag'));
        continue;
      }

      const rawParts = rawTag.split('|');
      const parts = rawParts.map((part) => parseTagPart(part, rawTag, issues));
      if (parts.some((part) => !part)) continue;

      if (parts.length === 1 && parts[0] === 'common') {
        tags.push({ type: 'common', key: 'common', parts: [], sort });
      } else if (parts.length === 1 && parts[0] === 'default') {
        tags.push({ type: 'default', key: 'default', parts: [], sort });
      } else {
        tags.push({
          type: 'variant',
          key: parts.join('|'),
          parts,
          sort,
        });
      }
    }

    if (parsedDirectiveCount < directiveStarts.length) {
      issues.push(createIssue('malformed-directive'));
    }

    const tagTypes = new Set(tags.map((tag) => tag.type));
    if (tagTypes.size > 1) {
      issues.push(createIssue('mixed-tag-types', Array.from(tagTypes).join('|')));
    }

    return {
      hasDirective,
      tags,
      issues,
    };
  }

  function createPmgEntry(media, fallbackIndex) {
    const source = media || {};
    const parsed = parsePmgTags(source.rawTags);
    const numericIndex = Number(source.index);
    const index = Number.isInteger(numericIndex) && numericIndex >= 0 ? numericIndex : fallbackIndex;

    return {
      index,
      mediaId: String(source.mediaId == null ? '' : source.mediaId),
      hasDirective: parsed.hasDirective,
      tags: parsed.tags,
      issues: parsed.issues,
    };
  }

  function preparePmgMedia(mediaItems) {
    if (!Array.isArray(mediaItems)) return [];
    return mediaItems.map((media, index) => createPmgEntry(media, index));
  }

  function doesPmgPartMatch(part, selectedPart) {
    return String(part || '')
      .split('&')
      .some((choice) => choice === selectedPart);
  }

  function isOrderedSubset(parts, selectedParts) {
    if (!Array.isArray(parts) || !parts.length || !Array.isArray(selectedParts)) return false;

    let selectedIndex = 0;
    for (const part of parts) {
      let found = false;
      while (selectedIndex < selectedParts.length) {
        if (doesPmgPartMatch(part, selectedParts[selectedIndex])) {
          found = true;
          selectedIndex += 1;
          break;
        }
        selectedIndex += 1;
      }
      if (!found) return false;
    }

    return true;
  }

  function isPositionalMatch(parts, selectedParts) {
    if (
      !Array.isArray(parts) ||
      !Array.isArray(selectedParts) ||
      parts.length !== selectedParts.length
    ) {
      return false;
    }

    return parts.every(
      (part, index) => part === '*' || doesPmgPartMatch(part, selectedParts[index])
    );
  }

  function getMatchingTags(entry, selectedParts, aliasParts) {
    if (!entry) return [];
    const legacyParts = [...selectedParts, ...aliasParts];
    return entry.tags.filter((tag) => {
      if (tag.type !== 'variant') return false;
      return tag.parts.includes('*')
        ? isPositionalMatch(tag.parts, selectedParts)
        : isOrderedSubset(tag.parts, legacyParts);
    });
  }

  function isCommon(entry) {
    if (!entry) return false;
    return !entry.hasDirective || entry.tags.some((tag) => tag.type === 'common');
  }

  function isDefault(entry) {
    return Boolean(entry && entry.tags.some((tag) => tag.type === 'default'));
  }

  function inspectPmgEntry(entry, selection) {
    const selectedParts = normalizeOptions(selection && selection.options);
    const aliasParts = normalizeOptions(selection && selection.aliases);
    return {
      matchedKeys: getMatchingTags(entry, selectedParts, aliasParts).map((tag) => tag.key),
      common: isCommon(entry),
      default: isDefault(entry),
      hasDirective: Boolean(entry && entry.hasDirective),
      issues: entry && Array.isArray(entry.issues) ? entry.issues.slice() : [],
    };
  }

  function getTagSort(tags) {
    const sortedValues = tags
      .map((tag) => tag.sort)
      .filter((sort) => Number.isSafeInteger(sort));
    return sortedValues.length ? Math.min(...sortedValues) : UNSORTED;
  }

  function sortEntries(entries, getTags) {
    return entries.slice().sort((entryA, entryB) => {
      const sortA = getTagSort(getTags(entryA));
      const sortB = getTagSort(getTags(entryB));
      if (sortA !== sortB) return sortA - sortB;
      return entryA.index - entryB.index;
    });
  }

  function uniqueIndexes(groups) {
    const indexes = [];
    const seen = new Set();

    groups.flat().forEach((entry) => {
      if (!entry || seen.has(entry.index)) return;
      seen.add(entry.index);
      indexes.push(entry.index);
    });

    return indexes;
  }

  function resolvePmgMedia(entries, selection) {
    const mediaEntries = Array.isArray(entries) ? entries.filter(Boolean) : [];
    if (!mediaEntries.length) {
      return { indexes: [], reason: 'empty' };
    }

    if (!mediaEntries.some((entry) => entry.hasDirective)) {
      return {
        indexes: mediaEntries.map((entry) => entry.index),
        reason: 'all',
      };
    }

    const selectedParts = normalizeOptions(selection && selection.options);
    const aliasParts = normalizeOptions(selection && selection.aliases);
    const matchingTags = new Map(
      mediaEntries.map((entry) => [entry.index, getMatchingTags(entry, selectedParts, aliasParts)])
    );
    const matchedEntries = mediaEntries.filter((entry) => matchingTags.get(entry.index).length > 0);
    const commonEntries = mediaEntries.filter(isCommon);
    const defaultEntries = mediaEntries.filter(isDefault);

    if (matchedEntries.length) {
      return {
        indexes: uniqueIndexes([
          sortEntries(matchedEntries, (entry) => matchingTags.get(entry.index)),
          sortEntries(commonEntries, (entry) => entry.tags.filter((tag) => tag.type === 'common')),
        ]),
        reason: 'matched',
      };
    }

    if (defaultEntries.length) {
      return {
        indexes: uniqueIndexes([
          sortEntries(defaultEntries, (entry) => entry.tags.filter((tag) => tag.type === 'default')),
          sortEntries(commonEntries, (entry) => entry.tags.filter((tag) => tag.type === 'common')),
        ]),
        reason: 'default',
      };
    }

    const featuredMediaId = String(
      selection && selection.featuredMediaId != null ? selection.featuredMediaId : ''
    );
    const featuredEntry = featuredMediaId
      ? mediaEntries.find((entry) => entry.mediaId === featuredMediaId)
      : null;

    if (featuredEntry) {
      return {
        indexes: uniqueIndexes([
          [featuredEntry],
          sortEntries(commonEntries, (entry) => entry.tags.filter((tag) => tag.type === 'common')),
        ]),
        reason: 'featured',
      };
    }

    if (commonEntries.length) {
      return {
        indexes: uniqueIndexes([
          sortEntries(commonEntries, (entry) => entry.tags.filter((tag) => tag.type === 'common')),
        ]),
        reason: 'common',
      };
    }

    return {
      indexes: [mediaEntries[0].index],
      reason: 'fallback',
    };
  }

  function stripPmgTags(value) {
    return String(value == null ? '' : value)
      .replace(/\s*\[pmg(?:-[^:\]\s]+)?:[^\]]*\]\s*/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  return Object.freeze({
    normalizePmgValue,
    normalizeOptions,
    parsePmgTags,
    preparePmgMedia,
    isOrderedSubset,
    isPositionalMatch,
    inspectPmgEntry,
    resolvePmgMedia,
    stripPmgTags,
  });
});
